import React, { useRef, useState, useEffect } from "react";
import {
  TextAlignLeftIcon,
  TextAlignCenterIcon,
  TextAlignRightIcon,
  TextAlignJustifyIcon,
  VerticalAlignTopIcon,
  VerticalAlignMiddleIcon,
  VerticalAlignBottomIcon,
  TextColorIcon,
  BackgroundColorIcon,
  PencilColorIcon,
} from "../../SVGIcons";
import { EditorObject } from "../types/editorTypes";

interface TextFormattingOptionsProps {
  // 現在のツール設定
  textFontFamily: string;
  setTextFontFamily: (family: string) => void;
  textFontSize: number;
  setTextFontSize: (size: number) => void;
  textFontStyle: 'normal' | 'bold' | 'italic';
  setTextFontStyle: (style: 'normal' | 'bold' | 'italic') => void;
  textAlign: 'left' | 'center' | 'right' | 'justify';
  setTextAlign: (align: 'left' | 'center' | 'right' | 'justify') => void;
  verticalAlign: 'top' | 'middle' | 'bottom';
  setVerticalAlign: (align: 'top' | 'middle' | 'bottom') => void;
  textColor: string;
  setTextColor: (color: string) => void;
  textBgColor: string;
  setTextBgColor: (color: string) => void;
  // 選択中のオブジェクト
  selectedTextObject: EditorObject | null;
  selectedRectObject: EditorObject | null;
  // プロパティ更新関数
  updateTextProperty: (property: keyof EditorObject, value: any) => void;
  updateRectProperty: (property: keyof EditorObject, value: any) => void;
}

export const TextFormattingOptions: React.FC<TextFormattingOptionsProps> = ({
  textFontFamily,
  setTextFontFamily,
  textFontSize,
  setTextFontSize,
  textFontStyle,
  setTextFontStyle,
  textAlign,
  setTextAlign,
  verticalAlign,
  setVerticalAlign,
  textColor,
  setTextColor,
  textBgColor,
  setTextBgColor,
  selectedTextObject,
  selectedRectObject,
  updateTextProperty,
  updateRectProperty,
}) => {
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);
  const [showTextAlignDropdown, setShowTextAlignDropdown] = useState(false);
  const fontSizeDropdownRef = useRef<HTMLDivElement>(null);
  const textAlignDropdownRef = useRef<HTMLDivElement>(null);
  const textColorInputRef = useRef<HTMLInputElement>(null);
  const bgColorInputRef = useRef<HTMLInputElement>(null);
  const strokeColorInputRef = useRef<HTMLInputElement>(null);

  // フォントサイズドロップダウンの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fontSizeDropdownRef.current && !fontSizeDropdownRef.current.contains(e.target as Node)) {
        setShowFontSizeDropdown(false);
      }
    };
    if (showFontSizeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFontSizeDropdown]);

  // テキスト配置ドロップダウンの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (textAlignDropdownRef.current && !textAlignDropdownRef.current.contains(e.target as Node)) {
        setShowTextAlignDropdown(false);
      }
    };
    if (showTextAlignDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTextAlignDropdown]);

  const currentObj = selectedTextObject || selectedRectObject;

  return (
    <>
      {/* フォントファミリー */}
      <select
        value={currentObj?.fontFamily || textFontFamily}
        onChange={(e) => {
          if (selectedTextObject) {
            updateTextProperty('fontFamily', e.target.value);
          } else if (selectedRectObject) {
            updateRectProperty('fontFamily', e.target.value);
          } else {
            setTextFontFamily(e.target.value);
          }
        }}
        className="px-1 py-1 text-xs border border-gray-300 rounded h-8 ml-2"
        title="フォント"
      >
        <option value="'Hiragino Mincho Pro', 'Hiragino Mincho ProN', serif">ヒラギノ明朝</option>
        <option value="'Source Han Serif', 'Noto Serif CJK JP', serif">源ノ明朝</option>
        <option value="'Yu Mincho', 'YuMincho', '游明朝', serif">游明朝</option>
        <option value="Meiryo, 'メイリオ', sans-serif">メイリオ</option>
        <option value="'Yu Gothic', YuGothic, '游ゴシック', sans-serif">游ゴシック</option>
      </select>

      {/* フォントサイズ（－ 数値 ＋） */}
      <div className="flex items-center" ref={fontSizeDropdownRef}>
        <button
          onClick={() => {
            const currentSize = currentObj?.fontSize || textFontSize;
            const newSize = Math.max(1, currentSize - 1);
            if (selectedTextObject) {
              updateTextProperty('fontSize', newSize);
            } else if (selectedRectObject) {
              updateRectProperty('fontSize', newSize);
            } else {
              setTextFontSize(newSize);
            }
          }}
          className="px-2 py-1 text-gray-600 hover:bg-gray-100 h-8 flex items-center justify-center"
          title="フォントサイズを小さく"
        >
          −
        </button>
        <div className="relative">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={currentObj?.fontSize || textFontSize}
            onChange={(e) => {
              const num = Number(e.target.value);
              if (!isNaN(num) && num >= 1 && num <= 200) {
                if (selectedTextObject) {
                  updateTextProperty('fontSize', num);
                } else if (selectedRectObject) {
                  updateRectProperty('fontSize', num);
                } else {
                  setTextFontSize(num);
                }
              }
            }}
            onClick={() => setShowFontSizeDropdown(!showFontSizeDropdown)}
            className="px-1 py-1 text-xs text-center border border-gray-300 rounded w-12 h-8 cursor-pointer"
            title="クリックでプリセット選択"
          />
          {showFontSizeDropdown && (
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 max-h-64 overflow-y-auto min-w-[70px] py-2">
              {[6, 7, 8, 9, 10, 11, 12, 14, 18, 24, 30, 36, 48, 60, 72, 96].map((size) => (
                <button
                  key={size}
                  onClick={() => {
                    if (selectedTextObject) {
                      updateTextProperty('fontSize', size);
                    } else if (selectedRectObject) {
                      updateRectProperty('fontSize', size);
                    } else {
                      setTextFontSize(size);
                    }
                    setShowFontSizeDropdown(false);
                  }}
                  className={`block w-full px-4 py-2 text-sm text-center hover:bg-gray-100 ${
                    (currentObj?.fontSize || textFontSize) === size ? 'bg-gray-100 font-medium' : ''
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => {
            const currentSize = currentObj?.fontSize || textFontSize;
            const newSize = Math.min(200, currentSize + 1);
            if (selectedTextObject) {
              updateTextProperty('fontSize', newSize);
            } else if (selectedRectObject) {
              updateRectProperty('fontSize', newSize);
            } else {
              setTextFontSize(newSize);
            }
          }}
          className="px-2 py-1 text-gray-600 hover:bg-gray-100 h-8 flex items-center justify-center"
          title="フォントサイズを大きく"
        >
          +
        </button>
      </div>

      {/* 太字 */}
      <button
        onClick={() => {
          if (selectedTextObject) {
            updateTextProperty('fontStyle', selectedTextObject.fontStyle === 'bold' ? 'normal' : 'bold');
          } else if (selectedRectObject) {
            updateRectProperty('fontStyle', selectedRectObject.fontStyle === 'bold' ? 'normal' : 'bold');
          } else {
            setTextFontStyle(textFontStyle === 'bold' ? 'normal' : 'bold');
          }
        }}
        className={`p-1.5 rounded font-bold ${
          (currentObj?.fontStyle || textFontStyle) === 'bold' ? 'bg-orange-500 text-white' : 'hover:bg-gray-100'
        }`}
        title="太字"
      >
        <span className="w-5 h-5 flex items-center justify-center text-sm">B</span>
      </button>

      {/* テキスト配置 */}
      <div className="relative ml-1" ref={textAlignDropdownRef}>
        <button
          onClick={() => setShowTextAlignDropdown(!showTextAlignDropdown)}
          className="p-1.5 rounded hover:bg-gray-100 flex items-center gap-1"
          title="テキスト配置"
        >
          {(currentObj?.textAlign || textAlign) === 'left' && <TextAlignLeftIcon className="w-4 h-4" />}
          {(currentObj?.textAlign || textAlign) === 'center' && <TextAlignCenterIcon className="w-4 h-4" />}
          {(currentObj?.textAlign || textAlign) === 'right' && <TextAlignRightIcon className="w-4 h-4" />}
          {(currentObj?.textAlign || textAlign) === 'justify' && <TextAlignJustifyIcon className="w-4 h-4" />}
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showTextAlignDropdown && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 p-1">
            {/* 水平方向配置 */}
            <div className="flex gap-0.5 mb-1">
              {(['left', 'center', 'right', 'justify'] as const).map((align) => (
                <button
                  key={align}
                  onClick={() => {
                    if (selectedTextObject) {
                      updateTextProperty('textAlign', align);
                    } else if (selectedRectObject) {
                      updateRectProperty('textAlign', align);
                    } else {
                      setTextAlign(align);
                    }
                  }}
                  className={`p-1.5 rounded ${
                    (currentObj?.textAlign || textAlign) === align ? 'bg-blue-100' : 'hover:bg-gray-100'
                  }`}
                  title={
                    align === 'left' ? '左揃え' :
                    align === 'center' ? '中央揃え' :
                    align === 'right' ? '右揃え' : '両端揃え'
                  }
                >
                  {align === 'left' && <TextAlignLeftIcon className="w-4 h-4" />}
                  {align === 'center' && <TextAlignCenterIcon className="w-4 h-4" />}
                  {align === 'right' && <TextAlignRightIcon className="w-4 h-4" />}
                  {align === 'justify' && <TextAlignJustifyIcon className="w-4 h-4" />}
                </button>
              ))}
            </div>
            {/* 垂直方向配置 */}
            <div className="flex gap-0.5">
              {(['top', 'middle', 'bottom'] as const).map((align) => (
                <button
                  key={align}
                  onClick={() => {
                    if (selectedTextObject) {
                      updateTextProperty('verticalAlign', align);
                    } else if (selectedRectObject) {
                      updateRectProperty('verticalAlign', align);
                    } else {
                      setVerticalAlign(align);
                    }
                  }}
                  className={`p-1.5 rounded ${
                    (currentObj?.verticalAlign || verticalAlign) === align ? 'bg-blue-100' : 'hover:bg-gray-100'
                  }`}
                  title={
                    align === 'top' ? '上揃え' :
                    align === 'middle' ? '中央揃え' : '下揃え'
                  }
                >
                  {align === 'top' && <VerticalAlignTopIcon className="w-4 h-4" />}
                  {align === 'middle' && <VerticalAlignMiddleIcon className="w-4 h-4" />}
                  {align === 'bottom' && <VerticalAlignBottomIcon className="w-4 h-4" />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 文字色 */}
      <div className="relative">
        <button
          onClick={() => textColorInputRef.current?.click()}
          className="p-1.5 rounded hover:bg-gray-100"
          title="文字色"
        >
          <TextColorIcon className="w-5 h-5" barColor={currentObj?.textColor || textColor} />
        </button>
        <input
          ref={textColorInputRef}
          type="color"
          value={currentObj?.textColor || textColor}
          onChange={(e) => {
            if (selectedTextObject) {
              updateTextProperty('textColor', e.target.value);
            } else if (selectedRectObject) {
              updateRectProperty('textColor', e.target.value);
            } else {
              setTextColor(e.target.value);
            }
          }}
          style={{ position: 'absolute', top: '100%', left: 0, visibility: 'hidden' }}
        />
      </div>

      {/* 背景色 - テキスト・四角形共通 */}
      <div className="relative">
        <button
          onClick={() => bgColorInputRef.current?.click()}
          className="p-1.5 rounded hover:bg-gray-100"
          title="背景色"
        >
          <BackgroundColorIcon
            className="w-5 h-5"
            barColor={(currentObj?.backgroundColor || textBgColor) === 'transparent' ? '#cccccc' : (currentObj?.backgroundColor || textBgColor)}
          />
        </button>
        <input
          ref={bgColorInputRef}
          type="color"
          value={(currentObj?.backgroundColor || textBgColor) === 'transparent' ? '#ffffff' : (currentObj?.backgroundColor || textBgColor)}
          onChange={(e) => {
            if (selectedTextObject) {
              updateTextProperty('backgroundColor', e.target.value);
            } else if (selectedRectObject) {
              updateRectProperty('backgroundColor', e.target.value);
            } else {
              setTextBgColor(e.target.value);
            }
          }}
          style={{ position: 'absolute', top: '100%', left: 0, visibility: 'hidden' }}
        />
      </div>
      {/* 背景透明ボタン */}
      <button
        onClick={() => {
          if (selectedTextObject) {
            updateTextProperty('backgroundColor', 'transparent');
          } else if (selectedRectObject) {
            updateRectProperty('backgroundColor', 'transparent');
          } else {
            setTextBgColor('transparent');
          }
        }}
        className="px-1.5 py-1 rounded text-xs hover:bg-gray-100 text-gray-600"
        title="背景を透明にする"
      >
        ✕
      </button>

      {/* 枠線色 - テキスト・四角形共通 */}
      <div className="relative">
        <button
          onClick={() => {
            if (!currentObj?.stroke) {
              // 枠線がない場合、まず枠線を有効化
              if (selectedTextObject) {
                updateTextProperty('stroke', '#000000');
                updateTextProperty('strokeWidth', 2);
              } else if (selectedRectObject) {
                updateRectProperty('stroke', '#000000');
                updateRectProperty('strokeWidth', 2);
              }
            }
            setTimeout(() => {
              strokeColorInputRef.current?.click();
            }, 10);
          }}
          className={`p-1.5 rounded ${currentObj?.stroke ? 'bg-gray-200' : 'hover:bg-gray-100 opacity-50'}`}
          title="枠線色"
        >
          <PencilColorIcon className="w-5 h-5" barColor={currentObj?.stroke || '#cccccc'} />
        </button>
        <input
          ref={strokeColorInputRef}
          type="color"
          value={currentObj?.stroke || '#000000'}
          onChange={(e) => {
            if (selectedTextObject) {
              updateTextProperty('stroke', e.target.value);
            } else if (selectedRectObject) {
              updateRectProperty('stroke', e.target.value);
            }
          }}
          style={{ position: 'absolute', top: '100%', left: 0, visibility: 'hidden' }}
        />
      </div>
      {/* 枠線なしボタン */}
      {currentObj?.stroke && (
        <button
          onClick={() => {
            if (selectedTextObject) {
              updateTextProperty('stroke', undefined);
              updateTextProperty('strokeWidth', 0);
            } else if (selectedRectObject) {
              updateRectProperty('stroke', undefined);
              updateRectProperty('strokeWidth', 0);
            }
          }}
          className="px-1.5 py-1 rounded text-xs hover:bg-gray-100 text-gray-600"
          title="枠線を無効にする"
        >
          ✕
        </button>
      )}
    </>
  );
};

export default TextFormattingOptions;
